import { DatePipe } from '@angular/common';
import { ValidationErrors } from '@angular/forms';

import {
	debounceTime,
	filter,
	finalize,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs/operators';

import {
	Observable,
	Observer,
	merge,
	of,
} from 'rxjs';

import { Amount } from 'ib/internetbank-core/ib-models/amount';
import { DateUtils, ISODateString } from 'ib/internetbank-core/utils/date-utils';


import { CustomValidators } from '../../../../common/custom-validation/custom-validators';
import { AmountInCurrencyPipe } from '../../../../common/pipes/amount-in-currency.pipe';
import { WizardDataStep } from '../../../../shared/wizard-v2/wizard-core/steps/base-step-types/wizard-data-step';
import { DatePickerInputComponent } from '../../../../ui/controls/date-picker-input/date-picker-input.component';
import { FieldOptions } from '../../../../ui/controls/field.model';
import { ProgressLabelFieldComponent } from '../../../../ui/controls/progress-label-field/progress-label-field.component';
import { SelectItem } from '../../../../ui/controls/select-field/select-item';
import { CustomFormControl } from '../../../../ui/custom-reactive-forms/custom-form-control';
import { CustomFormGroup } from '../../../../ui/custom-reactive-forms/custom-form-group';
import { FormAccessorUtils, SafetyFormAccessor } from '../../../../ui/form/form.model';
import { InsuranceIdByTypes } from '../../insurance';
import { InsuranceSportCreateWizardComponent } from '../insurance-sport-create-wizard.component';
import {
	InsuranceSportAutocompletePersonalData,
	InsuranceSportForm,
	InsuranceSportPremiumSum,
	InsuranceSportTypes,
	MAX_LIMIT_INSURED,
	MIN_LIMIT_DAYS_FOR_ROUND_THE_CLOCK_POLICY,
} from '../insurance-sport-create-wizard.model';


export class InsuranceSportCreateWizardFirstStepInsuranceParams extends WizardDataStep<InsuranceSportCreateWizardComponent> {

	public formAccessor: SafetyFormAccessor = {
		text: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'text'),
		insuranceRisks: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'insuranceRisks'),
		risksText: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'risksText'),
		sportTypes: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'sportTypes'),
		insuranceSum: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'insuranceSum'),
		checkoutForYourself: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'checkoutForYourself'),
		numberOfAdultsInput: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'numberOfAdultsInput'),
		numberOfChildrenInput: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'numberOfChildrenInput'),
		annual: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'annual'),
		policyValidityPeriod: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'policyValidityPeriod'),
		duration: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'duration'),
		premiumSum: (): CustomFormControl => FormAccessorUtils.getSafetyCustomFormControl(this.form, 'premiumSum'),
	};

	public clientInfo: InsuranceSportAutocompletePersonalData;
	public insuranceSportFormData: InsuranceSportForm;

	public onInit(): Observable<void> {
		this.wizard.setWizardPending(true);

		return this.wizardComponent.insuranceSportCreateWizardService.getInfo(InsuranceIdByTypes.SPORT)
			.pipe(
				tap((data: InsuranceSportForm) => {
					this.clientInfo = {
						email: data.email,
						...data.client,
					};
					this.insuranceSportFormData = data;
				}),
				switchMap((data: InsuranceSportForm) => this.createInitialForm(data)),
				finalize(() => this.wizard.setWizardPending(false)),
			);
	}

	public onExecute(): Observable<InsuranceSportAutocompletePersonalData> {
		return of(this.clientInfo);
	}

	private createInitialForm(data: InsuranceSportForm): Observable<void> {
		return new Observable((observer: Observer<void>) => {
			this.wizard.changeTitle(data.insuranceName);
			this.stepInformation.setSubmitLabel('Продолжить');

			const tomorrow: Date = DateUtils.getBeginningOfTomorrow();
			const nextYear: Date = DateUtils.getTheEndOfToday(DateUtils.getDateNextYear(new Date()));
			const periodStartMax: Date = new Date(new Date(tomorrow).setMonth(new Date(tomorrow).getMonth() + 1));

			this.form = new CustomFormGroup({
				text: new CustomFormControl(
					new FieldOptions({
						'@type': 'label-field',
						name: 'text',

						value: `Страхование от несчастного случая, в том числе, при занятиях любым видом спорта от <a href="${data.insuranceInfoPdfUrl}" target="_blank">АО "Русский Стандарт Страхование"</a>`,
					}),
				),
				insuranceRisks: new CustomFormControl(
					new FieldOptions({
						'@type': 'link-button-field',
						name: 'insuranceRisks',
						linkTitle: 'От чего застрахованы',
						onClick: () => {
							this.formAccessor.risksText().fieldOptions.visible = !this.formAccessor.risksText().fieldOptions.visible;
						},
					}),
				),
				risksText: new CustomFormControl(
					new FieldOptions({
						'@type': 'label-field',
						name: 'risksText',
						value: this.getInsuranceRisksList(data.insuranceRisks),
						visible: false,
						css: { classList: ['third-party-styles'] },
					}),
				),
				sportTypes: new CustomFormControl(
					new FieldOptions({
						'@type': 'combo-list',
						label: 'Виды спорта',
						name: 'sportTypes',
						hints: { underLineHint: 'Одновременно можно выбрать до 10-ти видов спорта' },
						items: data.sportTypes.map((item: InsuranceSportTypes) => ({
							id: item.id,
							value: item.name,
						})),
						required: true,
						maxLimit: 10,
						formControlWrapper: 'sport-types',
					}),
				),
				insuranceSum: new CustomFormControl(
					new FieldOptions({
						'@type': 'select-field',
						name: 'insuranceSum',
						label: 'Страховая сумма',
						value: null,
						items: data.insuranceSums.map((item: Amount) =>
							new SelectItem(
								new AmountInCurrencyPipe().transform(item)
									?.toString() || '',
								item.amount,
							),
						),
						getLabel: (el: InsuranceSportTypes): string => el.name,
						getValue: (el: InsuranceSportTypes): number => el.id,
						hints: { underLineHint: 'Максимальный размер страховых выплат' },
						css: { classList: ['insurance-sum'] },
						required: true,
					}),
				),
				checkoutForYourself: new CustomFormControl(
					new FieldOptions({
						'@type': 'toggle-field',
						label: 'Оформить на себя',
						name: 'checkoutForYourself',
					}),
				),
				addInsuredGroupTitle: new CustomFormControl(
					new FieldOptions({
						'@type': 'group-title-field',
						name: 'addInsuredGroupTitle',
						value: 'Добавить застрахованных',
						formControlWrapper: 'section-title',
					}),
				),
				numberOfAdults: new CustomFormGroup({
					numberOfAdultsText: new CustomFormControl(
						new FieldOptions({
							'@type': 'label-field',
							name: 'numberOfAdultsText',
							value: 'Взрослые (18-65 лет)',
							formControlWrapper: 'insured-text',
						}),
					),
					numberOfAdultsInput: new CustomFormControl(
						new FieldOptions({
							'@type': 'number-field',
							name: 'numberOfAdultsInput',
							value: 0,
							maxLimit: MAX_LIMIT_INSURED,
						}),
					),
				}, null, null, { formGroupWrapper: 'flex-group' }),
				numberOfChildren: new CustomFormGroup({
					numberOfChildrenText: new CustomFormControl(
						new FieldOptions({
							'@type': 'label-field',
							name: 'numberOfChildrenText',
							value: 'Дети (2-17 лет)',
							formControlWrapper: 'insured-text',
						}),
					),
					numberOfChildrenInput: new CustomFormControl(
						new FieldOptions({
							'@type': 'number-field',
							name: 'numberOfChildrenInput',
							value: 0,
							maxLimit: MAX_LIMIT_INSURED,
						}),
					),
				}, null, null, { formGroupWrapper: 'flex-group' }),
				annual: new CustomFormControl(
					new FieldOptions({
						'@type': 'toggle-field',
						label: 'Хочу полис на год',
						name: 'annual',
						value: false,
						hints: { underLineHint: 'Дата начала действия полиса должна быть не позднее одного месяца от текущей даты' },
					}),
				),
				policyValidityPeriod: new CustomFormControl(
					new FieldOptions({
						'@type': 'date-picker-input',
						label: 'Период действия полиса',
						name: 'policyValidityPeriod',
						itemsCount: 2,
						hints: { underLineHint: 'Дата начала и окончания включены в период действия полиса' },
						minDate: DateUtils.getBeginningOfToday(new Date(tomorrow)),
					}),
					[
						(control: CustomFormControl): ValidationErrors | null => {

							control.markAsFailureEnabled();

							if (!control.value) {
								return CustomValidators.createValidationError('Укажите значение поля');
							}

							const message = this.getValidatorMessageForPolicyValidityPeriod(
								new Date(control.value[0]),
								new Date(control.value[1]),
								control.fieldOptions.minDate,
								new Date(periodStartMax),
							);

							if (message) {
								return CustomValidators.createValidationError(message);
							}

							return null;

						},
					],
				),
				policyValidityPeriodGroupTitle: new CustomFormControl(
					new FieldOptions({
						'@type': 'group-title-field',
						name: 'policyValidityPeriodGroupTitle',
						value: 'Время действия полиса',
						formControlWrapper: 'section-title',
					}),
				),
				duration: new CustomFormControl(
					new FieldOptions({
						'@type': 'radio-button-field',
						name: 'durationId',
						value: 1,
						items: this.getDurationItems(true),
					}),
				),
				premiumSum: new CustomFormControl(
					new FieldOptions({
						'@type': 'progress-label-field',
						name: 'premiumSum',
						label: 'Предварительная стоимость полиса',
						value: '0.00 ₽',
					}),
				),
			},
				(form: CustomFormGroup): ValidationErrors | null => {
					if (form.getControlFromAllLevels('numberOfAdultsInput')?.value || form.getControlFromAllLevels('numberOfChildrenInput')?.value) {
						return null;
					}
					return { emptyInsuredMessage: true };
				});

			merge(
				this.formAccessor.numberOfAdultsInput().valueChanges,
				this.formAccessor.numberOfChildrenInput().valueChanges,
				this.formAccessor.policyValidityPeriod().valueChanges,
				this.formAccessor.duration().valueChanges,
				this.formAccessor.insuranceSum().valueChanges,
				this.formAccessor.sportTypes().valueChanges,
			)
				.pipe(
					debounceTime(this.wizardComponent.localDebounceTime),
					filter(() => this.formAccessor.sportTypes().value
						&& this.formAccessor.insuranceSum().value
						&& (this.formAccessor.policyValidityPeriod().value
							&& this.formAccessor.policyValidityPeriod().value[0]
							&& this.formAccessor.policyValidityPeriod().value[1]
						),
					),
					takeUntil(this.wizardComponent.ngUnsubscribe$$),
				)
				.subscribe(() => {
					this.calculatePremiumSum();
				});

			this.setMaxLimitForInsured(this.formAccessor.numberOfAdultsInput(), this.formAccessor.numberOfChildrenInput());
			this.setMaxLimitForInsured(this.formAccessor.numberOfChildrenInput(), this.formAccessor.numberOfAdultsInput());

			this.formAccessor.annual().valueChanges
				.pipe(
					filter((value: boolean) => !!value),
					takeUntil(this.wizardComponent.ngUnsubscribe$$),
				)
				.subscribe(() => {
					this.formAccessor.policyValidityPeriod().setValue([
						DateUtils.fromJsDateToDbDate(tomorrow),
						DateUtils.fromJsDateToDbDate(nextYear),
					]);
					const datePicker = (this.formAccessor.policyValidityPeriod().templateComponentRef as DatePickerInputComponent)
						.customFormGroup.getControlFromAllLevels('datePicker');
					datePicker?.updateValueAndValidity();
				});

			this.formAccessor.policyValidityPeriod().valueChanges
				.pipe(
					debounceTime(this.wizardComponent.localDebounceTime),
					filter((value: [ISODateString, ISODateString]) => !!value),
					takeUntil(this.wizardComponent.ngUnsubscribe$$),
				)
				.subscribe((value: [ISODateString, ISODateString]) => {

					if (this.formAccessor.annual().value) {
						const date = DateUtils.getTheEndOfToday(new Date(new Date(value[0]).setDate((new Date(value[0]).getDate() - 1))));
						const nextYearChanged = DateUtils.fromJsDateToDbDate(DateUtils.getDateNextYear(date));

						if (value[1] !== nextYearChanged) {
							this.formAccessor.policyValidityPeriod().setValue([
								value[0],
								nextYearChanged,
							]);
							const datePicker = (this.formAccessor.policyValidityPeriod().templateComponentRef as DatePickerInputComponent)
								.customFormGroup.getControlFromAllLevels('datePicker');
							datePicker?.updateValueAndValidity();
						}
					}

					const differenceDays: number = this.getDifferenceBetweenTwoDate(new Date(value[0]), new Date(value[1]));

					this.formAccessor.duration().fieldOptions.items = this.getDurationItems(differenceDays >= MIN_LIMIT_DAYS_FOR_ROUND_THE_CLOCK_POLICY);

					if (differenceDays < MIN_LIMIT_DAYS_FOR_ROUND_THE_CLOCK_POLICY) {
						this.formAccessor.duration().setValue(2);
					}

				});

			this.formAccessor.checkoutForYourself().valueChanges
				.pipe(takeUntil(this.wizardComponent.ngUnsubscribe$$))
				.subscribe((value: boolean) => {
					this.formAccessor.numberOfAdultsInput().setValue(
						this.formAccessor.numberOfAdultsInput().value + (value ? 1 : -1),
					);
				});

			this.formAccessor.numberOfAdultsInput().valueChanges
				.pipe(
					filter((value: number) => value === 0),
					takeUntil(this.wizardComponent.ngUnsubscribe$$),
				)
				.subscribe(() => {
					this.formAccessor.checkoutForYourself().setValue(false, { emitEvent: false });
				});


			observer.next();
			observer.complete();
		});
	}

	private getInsuranceRisksList(insuranceRisks: Array<string>): string {
		let list = '';

		insuranceRisks.forEach((item: string) => {
			list += `<li>${item}</li>`;
		});

		return `<ul>${list}</ul>`;
	}

	private getValidatorMessageForPolicyValidityPeriod(start: Date, end: Date, minDate: Date | undefined | null, periodStartMax: Date | undefined | null): string | undefined {
		switch (true) {
			case minDate && start < minDate:
				return `Дата начала периода не может быть меньше
				${new DatePipe('ru-RU').transform(
					minDate,
					'dd.MM.yyyy',
				)}`;

			case end < start:
				return 'Дата начала периода не может быть больше даты окончания';

			case periodStartMax && start >= periodStartMax:
				return 'Дата начала действия полиса должна быть не позднее одного месяца от текущей даты';

			case end >= DateUtils.getDateNextYear(start):
				return 'Возможный период страхования не больше года.';

			default:
				return;
		}
	}

	private getBodyForPremiumSumProductInsurance(): InsuranceSportPremiumSum {
		return {
			sportTypeIds: this.formAccessor.sportTypes().value,
			insuranceSum: this.formAccessor.insuranceSum().value,
			numberOfAdults: this.formAccessor.numberOfAdultsInput().value,
			numberOfChildren: this.formAccessor.numberOfChildrenInput().value,
			annual: this.formAccessor.annual().value,
			beginDate: this.formAccessor.policyValidityPeriod().value[0],
			endDate: this.formAccessor.policyValidityPeriod().value[1],
			durationId: this.formAccessor.duration().value,
		};
	}

	private setMaxLimitForInsured(control: CustomFormControl, additionalControl: CustomFormControl): void {
		control.valueChanges
			.pipe(takeUntil(this.wizardComponent.ngUnsubscribe$$))
			.subscribe((value: number) => {
				if (additionalControl.fieldOptions.maxLimit && additionalControl.fieldOptions.maxLimit >= 0) {
					additionalControl.fieldOptions.maxLimit = MAX_LIMIT_INSURED - value;
				}
				if (MAX_LIMIT_INSURED - additionalControl.value - value < 0) {
					control.setValue(MAX_LIMIT_INSURED - additionalControl.value, { emitEvent: false });
				}
				if ((+value + +additionalControl.value) >= MAX_LIMIT_INSURED && !this.formAccessor.checkoutForYourself().value) {
					this.formAccessor.checkoutForYourself().disable({ emitEvent: false });
				} else if (this.formAccessor.checkoutForYourself().fieldOptions.disabled) {
					this.formAccessor.checkoutForYourself().enable({ emitEvent: false });
				}
			});
	}

	private calculatePremiumSum(): void {
		(this.formAccessor.premiumSum().templateComponentRef as ProgressLabelFieldComponent).loading = true;
		this.wizardComponent.insuranceSportCreateWizardService.getPremiumSum(
			this.getBodyForPremiumSumProductInsurance(),
		)
			.subscribe((value: Amount) => {
				this.formAccessor.premiumSum().setValue(new AmountInCurrencyPipe().transform(value), { emitEvent: false });
				(this.formAccessor.premiumSum().templateComponentRef as ProgressLabelFieldComponent).loading = false;
			});
	}

	private getDifferenceBetweenTwoDate(date1: Date, date2: Date): number {
		const startDate1Time: number = DateUtils.getTheEndOfToday(date2).getTime();
		const endDate2Time: number = DateUtils.getBeginningOfToday(date1).getTime();

		return Math.round((startDate1Time - endDate2Time) / (1000 * 60 * 60 * 24));
	}

	private getDurationItems(isAllItems: boolean): Array<{
		label: string;
		value: number
	}> {
		const items = [
			{
				label: 'Круглосуточно',
				value: 1,
			},
			{
				label: 'Только на время занятий спортом',
				value: 2,
			},
		];

		return isAllItems ? items : [items[1]];
	}

}
